// frontend/src/app/components/onboarding/smoke-calibration-panel.tsx
//
// Smoke-Stage Rubric Calibration panel (SPEC: Smoke-Stage Rubric Calibration).
// The Smoke preset is the required pre-flight for the full baseline: it runs a
// small representative sample through the real probe + scoring path and reports
// per-variable rubric health (0/1/2 table + saturation/stuck/coverage flags +
// empty-response warnings). The full baseline (Execute) is gated behind a
// PASS / REVIEW state the operator must acknowledge.

import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Beaker, Check, AlertTriangle, Loader2, ShieldCheck, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";

import {
  fetchCalibrationGate,
  startCalibration,
  acknowledgeCalibration,
  decideCalibrationProposal,
  applyCalibrationRound,
  rediagnoseCalibration,
  type CalibrationGate,
  type CalibrationSummary,
  type CalibrationFlagKind,
} from "@/api/onboarding-measurement-api";
import { RubricResults } from "./rubric-results";
import type { BatchConfig } from "./onboarding-batch-config";
import {
  AcknowledgeForm,
  CalibrationHistoryView,
  DiagnosisCard,
  type DecideArgs,
} from "./calibration-diagnosis";

interface Props {
  customerId: number;
  frameworkApproved: boolean;
  /** Fired whenever the go/no-go gate state changes, so the parent can
   *  enable/disable the full-baseline Execute control. */
  onGateChange?: (open: boolean) => void;
  /** The Stage 4 picker selection. The calibration pre-flight must probe the
   *  SAME lanes (and versions) the full baseline will, so the operator is
   *  calibrating what they will actually run — not a fixed default subset. */
  selection?: BatchConfig;
  /** Fired when a calibration round revised or unlocked the framework, so
   *  the parent can reload the customer (#138). */
  onFrameworkChanged?: () => void;
}

const FLAG_LABEL: Record<CalibrationFlagKind, string> = {
  saturation: "Saturation",
  stuck: "Stuck",
  coverage: "Coverage",
  thin: "One question",
};

const DEFAULT_MAX_ROUNDS = 3;

export function SmokeCalibrationPanel({
  customerId,
  frameworkApproved,
  onGateChange,
  selection,
  onFrameworkChanged,
}: Props) {
  const [gate, setGate] = useState<CalibrationGate | null>(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [applying, setApplying] = useState(false);
  // One decision or apply in flight at a time: the server refuses a second.
  const [deciding, setDeciding] = useState(false);
  const [rediagnosing, setRediagnosing] = useState(false);
  // Waited as long as a diagnosis takes and none landed.
  const [diagnosisWaitOver, setDiagnosisWaitOver] = useState(false);
  const onGateChangeRef = useRef(onGateChange);
  onGateChangeRef.current = onGateChange;

  const load = useCallback(async () => {
    try {
      const g = await fetchCalibrationGate(customerId);
      setGate(g);
      onGateChangeRef.current?.(g.open);
    } catch (e: any) {
      toast.error(e?.message || "Failed to load calibration state");
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => {
    if (!frameworkApproved) {
      setLoading(false);
      return;
    }
    void load();
  }, [frameworkApproved, load]);

  // Poll while a calibration run is in flight so the summary appears when scoring
  // finishes (the run reuses the standard measurement + auto-pipeline path).
  // The run flips to 'completed' when PROBING is done; the judge then has to
  // score the sample before calibration_json exists, which in batch mode is
  // minutes, not a beat. This used to be a fixed ~32s post-completion wait, so
  // the panel spent the rest of the scoring pass showing "completed without a
  // summary. Re-run" over a run that was working perfectly. The server now says
  // whether the scoring pass is actually alive (`scoring_in_flight`), so the
  // wait follows the work: keep polling while it is, and only fall through to
  // the Re-run affordance once the judge is genuinely gone.
  const runStatus = gate?.calibration_run?.status;
  const runSummary = gate?.calibration_run?.calibration as CalibrationSummary | null | undefined;
  const hasSummary = !!runSummary;
  // The diagnosis is written just after the summary (#138), by the same
  // pipeline, so a flagged summary without one is still being diagnosed. Not a
  // calibration from before the battery hash, or one already acknowledged:
  // nothing is coming for those.
  const awaitingDiagnosis = !!runSummary && runSummary.flags.length > 0 && !runSummary.diagnosis
    && !!runSummary.battery_hash && !gate?.calibration_run?.calibration_acknowledged;
  const scoringInFlight = !!gate?.scoring_in_flight;
  const summaryWaitsRef = useRef(0);
  const calibrationRunId = gate?.calibration_run?.id;
  useEffect(() => setDiagnosisWaitOver(false), [calibrationRunId]);
  useEffect(() => {
    const inFlight = runStatus === "pending" || runStatus === "running";
    const awaitingSummary = runStatus === "completed" && (!hasSummary || awaitingDiagnosis);
    // Probing or scoring: either way the run is alive, so the grace counter
    // that gives up on it has not started.
    if (inFlight || scoringInFlight) summaryWaitsRef.current = 0;
    if (!inFlight && !awaitingSummary) return;
    const timer = setInterval(() => {
      if (!inFlight && !scoringInFlight) {
        summaryWaitsRef.current += 1;
        // Nothing is scoring and no summary has landed. Give the auto-pipeline a
        // short grace period to pick the run up (~32s), then stop and offer the
        // Re-run — at this point the run really is summary-less. A diagnosis is
        // one model call per flagged belief, so it gets ~5 minutes before the
        // panel stops waiting and offers "Diagnose again".
        if (summaryWaitsRef.current > (hasSummary ? 75 : 8)) {
          clearInterval(timer);
          if (hasSummary) setDiagnosisWaitOver(true);
          return;
        }
      }
      void load();
    }, 4000);
    return () => clearInterval(timer);
  }, [runStatus, hasSummary, awaitingDiagnosis, scoringInFlight, load]);

  const handleRun = async () => {
    setStarting(true);
    try {
      // Forward the picker's lane selection so calibration probes the same lanes
      // (and version pins) the full baseline will run. The server still sets the
      // query sample and reps=1; only the lane/version selection rides along.
      // Mirrors the fields the baseline start path sends (models / version_mode /
      // model_overrides / web_search_overrides / endpoint_overrides). Absent
      // selection → server default subset (legacy).
      await startCalibration(
        customerId,
        selection
          ? {
              batch_config: {
                models: selection.models,
                version_mode: selection.versionMode,
                model_overrides: selection.modelOverrides,
                endpoint_overrides: selection.endpointOverrides,
                // Grounding rides along too, so the calibration sample is
                // collected under the same regime as the baseline it gates.
                web_search_overrides: selection.webSearchOverrides,
              },
            }
          : {},
      );
      toast.info("Smoke calibration started — sampling and scoring…");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to start calibration");
    } finally {
      setStarting(false);
    }
  };

  const handleAcknowledge = async (reason: string) => {
    const runId = gate?.calibration_run?.id;
    if (!runId) return;
    try {
      await acknowledgeCalibration(customerId, runId, reason);
      toast.success("Flags acknowledged — reads on this rubric and battery are open.");
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to acknowledge");
    }
  };

  // A decision; the last one on a round applies it (server-side): the
  // framework is revised and relocked and the next round starts.
  const handleDecide = async ({ proposalId, action, edited, note }: DecideArgs) => {
    const runId = gate?.calibration_run?.id;
    if (!runId) return;
    setDeciding(true);
    try {
      const result = await decideCalibrationProposal(customerId, runId, proposalId, { action, edited, note });
      if (result.apply_error) {
        toast.error(result.apply_error);
      } else if (result.applied?.mode === "handed_to_stage3") {
        toast.info("Framework unlocked — replace the belief at Stage 2 or 3, approve, then calibrate again.");
        onFrameworkChanged?.();
      } else if (result.next_run) {
        toast.success(`Change applied and the framework relocked — round ${(result.applied?.round ?? 1) + 1} is running on the same sample.`);
        onFrameworkChanged?.();
      } else if (result.applied?.rerun_off) {
        toast.info("Change applied and the framework relocked. Automatic re-run is off, so the next round has not started.");
        onFrameworkChanged?.();
      }
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to record the decision");
    } finally {
      setDeciding(false);
    }
  };

  const handleApply = async () => {
    const runId = gate?.calibration_run?.id;
    if (!runId) return;
    setApplying(true);
    try {
      const result = await applyCalibrationRound(customerId, runId);
      toast.success(result.next_run ? "The next round is running." : "Framework unlocked.");
      onFrameworkChanged?.();
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to apply the round");
    } finally {
      setApplying(false);
    }
  };

  const handleRediagnose = async () => {
    const runId = gate?.calibration_run?.id;
    if (!runId) return;
    setRediagnosing(true);
    try {
      await rediagnoseCalibration(customerId, runId);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Failed to diagnose");
    } finally {
      setRediagnosing(false);
    }
  };

  if (!frameworkApproved) return null;

  const run = gate?.calibration_run ?? null;
  const summary = (run?.calibration as CalibrationSummary | undefined) ?? null;
  const inFlight = runStatus === "pending" || runStatus === "running";
  const acknowledged = !!run?.calibration_acknowledged;
  const round = run?.batch_config?.calibration_round?.number ?? 1;
  const maxRounds = gate?.history?.max_rounds ?? DEFAULT_MAX_ROUNDS;
  const diagnosis = summary?.diagnosis;
  const proposals = (diagnosis?.beliefs ?? []).flatMap((b) => (b.proposal ? [b.proposal] : []));
  const anyDecided = proposals.some((p) => p.status !== "pending");
  const canDecide = summary?.state === "REVIEW" && !acknowledged && !summary?.applied;
  // The automatic apply after the last decision was refused (e.g. a lint
  // failure fixed since): offer it again.
  const applyPending = canDecide && proposals.length > 0
    && !proposals.some((p) => p.status === "pending")
    && proposals.some((p) => p.status === "accepted");
  // The edit is applied and relocked but its re-run did not start.
  const rerunPending = !acknowledged && summary?.applied?.mode === "rerun" && !summary.applied.next_run_id;
  const rerunOff = gate?.automation?.rerun === false;
  const offerDiagnosis = diagnosis?.status === "failed" || diagnosis?.status === "partial" || diagnosis?.status === "off";
  const calibration = gate?.history?.calibrations?.[0];

  return (
    <div
      className="rounded-xl p-4 mb-4"
      style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)" }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div className="flex items-center gap-2">
          <Beaker className="w-4 h-4" style={{ color: "var(--alpha-teal)" }} />
          <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
            Smoke calibration — rubric pre-flight
          </span>
          {run && (
            <span style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
              Round {round} of {maxRounds}
              {calibration && gate?.history && ` · $${calibration.spent_usd.toFixed(2)} of $${gate.history.spend_cap_usd.toFixed(2)}`}
            </span>
          )}
        </div>
        {summary && <StateBadge state={summary.state} acknowledged={acknowledged} />}
      </div>

      {gate?.automation && (
        <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: "0 0 12px" }}>
          Automatic diagnosis {gate.automation.diagnosis ? "on" : "off"} · Automatic re-run {gate.automation.rerun ? "on" : "off"} · Set in Global Settings
        </p>
      )}

      {loading ? (
        <Row icon={<Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--alpha-teal)" }} />}>
          Checking calibration state…
        </Row>
      ) : inFlight ? (
        <Row icon={<Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--alpha-teal)" }} />}>
          Calibration running ({run?.progress_completed ?? 0} / {run?.progress_total ?? 0} calls) —
          the summary appears once scoring completes.
        </Row>
      ) : !run ? (
        <div className="space-y-3">
          <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)", margin: 0 }}>
            Run a small representative sample and score it with the real rubric to
            confirm the anchors are reachable, not saturated, and correctly mapped
            before committing to the full baseline.
          </p>
          <RunButton onClick={handleRun} busy={starting} label="Run Smoke calibration" />
        </div>
      ) : summary ? (
        <div className="space-y-3">
          <CalibrationTable summary={summary} />
          {summary.empty_response_warnings.length > 0 && (
            <EmptyWarnings summary={summary} />
          )}
          {/* The go/no-go call requires seeing what the rubric actually did —
              per-response scores AND the judge's evidence — not just the flag
              tally above. Reviewing the rubric is part of the gate. */}
          {run?.id != null && (
            <RubricResults
              customerId={customerId}
              runId={run.id}
              reasoningVariables={summary.per_variable.map((v) => ({ id: v.variable_id, claim: v.claim }))}
              runParams={[
                { label: "Rubric", value: summary.rubric_version },
                { label: "Sample", value: String(summary.sample_size) },
                { label: "Mapping", value: summary.mapping_mode },
              ]}
              title="Rubric evidence — review before running the full baseline"
              defaultExpanded={false}
            />
          )}
          {summary.state === "REVIEW" && summary.flags.length > 0 && (diagnosis || awaitingDiagnosis || canDecide) && (
            <div className="space-y-2">
              <div style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
                Diagnosis and proposed fixes
              </div>
              {!diagnosis && awaitingDiagnosis && !diagnosisWaitOver ? (
                <Row icon={<Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--alpha-teal)" }} />}>
                  Diagnosing the flagged beliefs from the sampled answers…
                </Row>
              ) : !diagnosis ? (
                <RunButton onClick={handleRediagnose} busy={rediagnosing} label="Diagnose" subtle />
              ) : (
                <>
                  {diagnosis.status === "off" && (
                    <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: 0 }}>
                      Automatic diagnosis was off when this calibration finished. An admin can still ask for one.
                    </p>
                  )}
                  {diagnosis.apply_blocked && (
                    <Row icon={<AlertTriangle className="w-4 h-4" style={{ color: "var(--infrared-coral)" }} />}>
                      {diagnosis.apply_blocked.message} {diagnosis.apply_blocked.detail}
                    </Row>
                  )}
                  {diagnosis.beliefs.map((belief) => (
                    <DiagnosisCard
                      key={belief.variable_id}
                      belief={belief}
                      round={round}
                      maxRounds={maxRounds}
                      canDecide={canDecide}
                      decisionInFlight={deciding || applying}
                      applyBlocked={!!diagnosis.apply_blocked}
                      onDecide={handleDecide}
                    />
                  ))}
                  {offerDiagnosis && canDecide && !anyDecided && (
                    <RunButton
                      onClick={handleRediagnose} busy={rediagnosing}
                      label={diagnosis.status === "off" ? "Diagnose" : "Diagnose again"} subtle
                    />
                  )}
                  {canDecide && proposals.length > 0 && !applyPending && (
                    <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", margin: 0 }}>
                      Accepted changes are applied together once every proposal is decided: the framework
                      is revised and relocked (recorded like a hand edit)
                      {rerunOff
                        ? `. Automatic re-run is off, so round ${round + 1} waits until you start it.`
                        : ` and the smoke re-runs on the same sample as round ${round + 1}.`}
                    </p>
                  )}
                  {applyPending && (
                    <RunButton onClick={handleApply} busy={applying || deciding} label="Apply accepted changes and re-run" />
                  )}
                </>
              )}
            </div>
          )}
          {summary.applied && (
            <Row icon={<Check className="w-4 h-4" style={{ color: "var(--chart-3)" }} />}>
              {summary.applied.mode === "rerun"
                ? summary.applied.next_run_id
                  ? `Applied by ${summary.applied.by}; re-running as run ${summary.applied.next_run_id}.`
                  : summary.applied.rerun_off
                    ? `Applied by ${summary.applied.by}. Automatic re-run is off: round ${summary.applied.round + 1} has not started.`
                    : `Applied by ${summary.applied.by}, but the re-run did not start: ${summary.applied.rerun_error ?? ""}`
                : `Framework unlocked by ${summary.applied.by} to replace a belief.`}
            </Row>
          )}
          {rerunPending && (
            <RunButton onClick={handleApply} busy={applying} label="Start the re-run" />
          )}
          {summary.acknowledgement && (
            <Row icon={<ShieldAlert className="w-4 h-4" style={{ color: "var(--alpha-teal)" }} />}>
              Acknowledged by {summary.acknowledgement.by}: “{summary.acknowledgement.reason}”
            </Row>
          )}
          <GateLine reason={gate?.reason ?? ""} open={!!gate?.open} />
          <div className="flex items-center gap-2 flex-wrap">
            {summary.state === "REVIEW" && canDecide && (
              <AcknowledgeForm onAcknowledge={handleAcknowledge} />
            )}
            <RunButton onClick={handleRun} busy={starting} label="Start a new calibration" subtle />
          </div>
          {gate?.history && <CalibrationHistoryView history={gate.history} />}
        </div>
      ) : // Probing is done but the judge is still working. That is a healthy run
      // mid-flight, not a failed one, so it must not offer to re-run it.
      gate?.scoring_in_flight ? (
        <Row icon={<Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--alpha-teal)" }} />}>
          Sample collected &mdash; scoring it now. The rubric table appears when the judge finishes.
        </Row>
      ) : (
        <Row icon={<AlertTriangle className="w-4 h-4" style={{ color: "var(--chart-3)" }} />}>
          Calibration run {run.status} without a summary.{" "}
          <button onClick={handleRun} style={{ color: "var(--alpha-teal)", background: "none", border: "none", cursor: "pointer", padding: 0, fontSize: "inherit", fontWeight: 600 }}>
            Re-run
          </button>
        </Row>
      )}
    </div>
  );
}

function StateBadge({ state, acknowledged }: { state: "PASS" | "REVIEW"; acknowledged: boolean }) {
  const pass = state === "PASS";
  const color = pass ? "var(--chart-3)" : acknowledged ? "var(--alpha-teal)" : "var(--infrared-coral)";
  const label = pass ? "PASS" : acknowledged ? "REVIEW · acknowledged" : "REVIEW";
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full"
      style={{ backgroundColor: "transparent", border: `1px solid ${color}`, color, fontSize: "var(--text-xs)", fontWeight: 700 }}
    >
      {pass ? <ShieldCheck className="w-3.5 h-3.5" /> : <ShieldAlert className="w-3.5 h-3.5" />}
      {label}
    </span>
  );
}

function CalibrationTable({ summary }: { summary: CalibrationSummary }) {
  return (
    <div className="rounded-lg overflow-hidden" style={{ border: "1px solid var(--border)" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "var(--text-xs)" }}>
        <thead>
          <tr style={{ backgroundColor: "var(--card)" }}>
            <Th align="left">Variable</Th>
            <Th>0</Th>
            <Th>1</Th>
            <Th>2</Th>
            <Th>%2</Th>
            <Th>%0</Th>
            <Th align="left">Flags</Th>
          </tr>
        </thead>
        <tbody>
          {summary.per_variable.map((row) => {
            const flagged = row.flags.length > 0;
            return (
              <tr key={row.variable_id} style={{ borderTop: "1px solid var(--border)", backgroundColor: flagged ? "rgba(245,158,11,0.06)" : "transparent" }}>
                <Td align="left">
                  <span style={{ fontWeight: 600, color: "var(--foreground)" }}>{row.variable_id}</span>
                  <span title={row.claim} style={{ color: "var(--muted-foreground)", marginLeft: 6 }}>
                    {row.claim.length > 48 ? row.claim.slice(0, 48) + "…" : row.claim}
                  </span>
                </Td>
                <Td>{row.score_0}</Td>
                <Td>{row.score_1}</Td>
                <Td>{row.score_2}</Td>
                <Td>{(row.pct_2 * 100).toFixed(0)}%</Td>
                <Td>{(row.pct_0 * 100).toFixed(0)}%</Td>
                <Td align="left">
                  {row.flags.length === 0 ? (
                    <Check className="w-3.5 h-3.5" style={{ color: "var(--chart-3)" }} />
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {row.flags.map((f) => <FlagChip key={f} kind={f} />)}
                    </span>
                  )}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function FlagChip({ kind }: { kind: CalibrationFlagKind }) {
  return (
    <span
      className="inline-block px-1.5 py-0.5 rounded"
      style={{ backgroundColor: "rgba(220,38,38,0.10)", color: "var(--infrared-coral)", fontSize: "10px", fontWeight: 700 }}
    >
      {FLAG_LABEL[kind]}
    </span>
  );
}

function EmptyWarnings({ summary }: { summary: CalibrationSummary }) {
  const n = summary.empty_response_warnings.length;
  return (
    <div className="flex items-start gap-2 p-2 rounded-md" style={{ backgroundColor: "rgba(245,158,11,0.08)", border: "1px solid var(--chart-3)" }}>
      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: "var(--chart-3)" }} />
      <span style={{ fontSize: "var(--text-xs)", color: "var(--foreground)" }}>
        {n} empty / blank response{n === 1 ? "" : "s"} detected and excluded from the
        0/1/2 tally (not counted as a real Score 0).
      </span>
    </div>
  );
}

function GateLine({ reason, open }: { reason: string; open: boolean }) {
  return (
    <div className="flex items-center gap-1.5" style={{ fontSize: "var(--text-xs)", color: open ? "var(--chart-3)" : "var(--muted-foreground)" }}>
      {open ? <Check className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
      <span>{reason}</span>
    </div>
  );
}

function RunButton({ onClick, busy, label, subtle }: { onClick: () => void; busy: boolean; label: string; subtle?: boolean }) {
  return (
    <button
      onClick={onClick}
      disabled={busy}
      className="flex items-center gap-2 px-3 py-1.5 rounded-md"
      style={{
        backgroundColor: subtle ? "transparent" : "var(--alpha-teal)",
        color: subtle ? "var(--alpha-teal)" : "white",
        border: subtle ? "1px solid var(--alpha-teal)" : "none",
        fontSize: "var(--text-xs)", fontWeight: 600,
        cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1,
      }}
    >
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Beaker className="w-3.5 h-3.5" />}
      {label}
    </button>
  );
}

function Row({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 py-1">
      {icon}
      <span style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>{children}</span>
    </div>
  );
}

function Th({ children, align = "center" }: { children: React.ReactNode; align?: "left" | "center" }) {
  return (
    <th style={{ padding: "6px 8px", textAlign: align, color: "var(--muted-foreground)", fontWeight: 600 }}>
      {children}
    </th>
  );
}

function Td({ children, align = "center" }: { children: React.ReactNode; align?: "left" | "center" }) {
  return (
    <td style={{ padding: "6px 8px", textAlign: align, color: "var(--foreground)" }}>
      {children}
    </td>
  );
}
